import { useEffect, useMemo, useState } from 'react'
import type { APIProductManufacturer } from '@visorpro/client'
import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table'
import {
    Box,
    Button,
    Dialog,
    Stack,
    TextField,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Snackbar,
    Chip,
} from '@mui/material'
import type { APICreateManufacturerRequest } from '@visorpro/client/dist/RestClient/services'
import { formatDate } from '../util/format'
import type { Stores } from '../util/store'
import { useAdminMaterialReactTable } from './table/mui-table'
import { createdByColumnDef } from './table/created-by-column'

export interface ManufacturersTableProps {
    manufacturers: APIProductManufacturer[]
    stores: Stores
    isLoading?: boolean
    showProgressBars?: boolean
    rowCount?: number
    isPrimaryBackgroundColor?: boolean
    createManufacturer?: (body: APICreateManufacturerRequest) => Promise<void>
    deleteManufacturer?: (manufacturerId: string) => Promise<void>
}

export const ManufacturersTable = ({
    manufacturers,
    stores,
    rowCount,
    isLoading,
    showProgressBars,
    isPrimaryBackgroundColor,
    createManufacturer,
    deleteManufacturer,
}: ManufacturersTableProps) => {
    const [isCreatingManufacturer, setIsCreatingManufacturer] = useState(false)
    const [isDeletingManufacturers, setIsDeletingManufacturers] =
        useState(false)

    const [
        manufacturersDeletedSnackbarOpen,
        setManufacturersDeletedSnackbarOpen,
    ] = useState(false)

    const columns = useMemo<MRT_ColumnDef<APIProductManufacturer>[]>(
        () => [
            {
                accessorKey: 'id',
                header: 'ID',
                enableClickToCopy: true,
                size: 200,
            },
            {
                accessorKey: 'name',
                header: 'Name',
                enableClickToCopy: true,
                size: 400,
            },
            {
                accessorFn: (doc) => doc.updated_at,
                header: 'Updated At',
                id: 'updated_at',
                Cell: ({ row }) => formatDate(row.getValue('updated_at')),
                filterVariant: 'datetime-range',
            },
            {
                accessorFn: (doc) => doc.created_at,
                header: 'Created At',
                id: 'created_at',
                Cell: ({ row }) => formatDate(row.getValue('created_at')),
                filterVariant: 'datetime-range',
            },
            createdByColumnDef(stores),
        ],
        [stores],
    )

    const table = useAdminMaterialReactTable(
        {
            columns,
            data: manufacturers,
            rowCount,
            enableRowSelection: true,
            enableSorting: true,
            initialState: {
                columnVisibility: {
                    id: false,
                    name: true,
                },
            },
            defaultColumn: {
                enableColumnFilter: true,
                enableSorting: true,
            },
            getRowId: (doc) => doc.id,
            state: {
                isLoading,
                showProgressBars,
            },
            renderTopToolbarCustomActions: ({ table }) => {
                const onCreateManufacturer = () =>
                    setIsCreatingManufacturer(true)
                const onDeleteManufacturers = () =>
                    setIsDeletingManufacturers(true)
                const selectedRows = table.getSelectedRowModel().flatRows

                return (
                    <Box sx={{ display: 'flex', gap: '0.5rem' }}>
                        {createManufacturer && (
                            <Button onClick={onCreateManufacturer}>
                                Create manufacturer
                            </Button>
                        )}
                        {deleteManufacturer && (
                            <Button
                                onClick={onDeleteManufacturers}
                                disabled={selectedRows.length === 0}>
                                {selectedRows.length > 1
                                    ? `Delete ${selectedRows.length} manufacturers`
                                    : 'Delete manufacturers'}
                            </Button>
                        )}
                    </Box>
                )
            },
            createDisplayMode: 'custom',
            isPrimaryBackgroundColor,
        },
        'manufacturers-table',
    )

    const onDeleteManufacturers = async (manufacturerIds: string[]) => {
        await Promise.allSettled(
            manufacturerIds.map((manufacturerId) =>
                deleteManufacturer?.(manufacturerId),
            ),
        )
        table.resetRowSelection()
        setManufacturersDeletedSnackbarOpen(true)
        setIsDeletingManufacturers(false)
    }

    const onManufacturersDeletedSnackbarClose = () =>
        setManufacturersDeletedSnackbarOpen(false)

    return (
        <>
            <MaterialReactTable table={table} />
            <CreateManufacturerDialog
                open={isCreatingManufacturer}
                setOpen={setIsCreatingManufacturer}
                createManufacturer={createManufacturer}
            />
            <DeleteManufacturerDialog
                open={isDeletingManufacturers}
                setOpen={setIsDeletingManufacturers}
                selectedManufacturers={table
                    .getSelectedRowModel()
                    .flatRows.map(
                        (row) => row.original as APIProductManufacturer,
                    )}
                deleteManufacturers={onDeleteManufacturers}
            />
            <Snackbar
                open={manufacturersDeletedSnackbarOpen}
                autoHideDuration={2000}
                onClose={onManufacturersDeletedSnackbarClose}
                message="Manufacturers deleted successfully"
            />
        </>
    )
}

interface CreateManufacturerDialogProps {
    open: boolean
    setOpen: (open: boolean) => void
    createManufacturer?: (manufacturer: APICreateManufacturerRequest) => void
}

const CreateManufacturerDialog = ({
    open,
    setOpen,
    createManufacturer,
}: CreateManufacturerDialogProps) => {
    const [name, setName] = useState<string>('')

    const onCreate = () => {
        createManufacturer?.({
            name,
        })
        onClose()
    }
    const onClose = () => setOpen(false)

    useEffect(() => {
        if (open) {
            setName('')
        }
    }, [open])

    return (
        <Dialog open={open} onClose={onClose} fullWidth>
            <DialogTitle>Create manufacturer</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    <Stack spacing={1} sx={{ py: 1 }}>
                        <TextField
                            required
                            id="create-manufacturer-name"
                            label="Name"
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                        />
                    </Stack>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    color="primary"
                    onClick={onCreate}
                    disabled={name === ''}>
                    Create
                </Button>
            </DialogActions>
        </Dialog>
    )
}

interface DeleteManufacturerDialogProps {
    open: boolean
    setOpen: (open: boolean) => void
    selectedManufacturers: APIProductManufacturer[]
    deleteManufacturers: (manufacturerIds: string[]) => void
}

const DeleteManufacturerDialog = ({
    open,
    setOpen,
    selectedManufacturers,
    deleteManufacturers,
}: DeleteManufacturerDialogProps) => {
    const onDelete = () => {
        deleteManufacturers(selectedManufacturers.map((manufacturer) => manufacturer.id))
        onClose()
    }
    const onClose = () => setOpen(false)

    return (
        <Dialog open={open} onClose={onClose} fullWidth>
            <DialogTitle>Delete manufacturers</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    <div>
                        Are you sure you want to delete the following{' '}
                        {selectedManufacturers.length} manufacturers?
                    </div>
                    <Box
                        sx={{
                            marginTop: 1,
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.5,
                        }}>
                        {selectedManufacturers.map((manufacturer, index) => (
                            <Chip
                                label={manufacturer.name}
                                title={manufacturer.name}
                                key={index}
                            />
                        ))}
                    </Box>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button color="primary" onClick={onDelete}>
                    Delete
                </Button>
            </DialogActions>
        </Dialog>
    )
}
